// useOnlineStatus.js
// Tracks connectivity and lets a component run a callback the moment
// the app comes back online — this is the "background update when
// back online" half of the offline-first pattern.
//
// Usage in Chat.js:
//
//   const isOnline = useOnlineStatus(() => {
//     updateContactMessaged();
//     fetchCategories(1, false);
//   });
//
// The callback fires once per online transition, not on every render.

import { useEffect, useRef, useState } from 'react';

export default function useOnlineStatus(onReconnect) {
  const [online, setOnline] = useState(navigator.onLine);
  const wasOffline = useRef(!navigator.onLine);
  const callbackRef = useRef(onReconnect);

  // Keep the latest callback without re-subscribing listeners
  useEffect(() => {
    callbackRef.current = onReconnect;
  }, [onReconnect]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      if (wasOffline.current) {
        wasOffline.current = false;
        callbackRef.current?.();
      }
    };

    const handleOffline = () => {
      setOnline(false);
      wasOffline.current = true;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also listen for the service worker telling us a queued
    // background-synced request (chat message, like, follow) finally
    // went through, so the UI can refresh from IndexedDB.
    const handleSWMessage = (event) => {
      if (event.data?.type === 'BG_SYNC_COMPLETE') {
        callbackRef.current?.();
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);

  return online;
}
